// RoomChat.jsx — Live chat sidebar
// Props: chat, members, host, username, onSendMessage, onSendReaction

import { useState, useEffect, useRef } from 'react';

const EMOJIS = ['🔥', '😭', '💀', '👑', '✨', '❤️', '🎵', '🤯'];

export default function RoomChat({ chat = [], members = [], host, username, onSendMessage, onSendReaction }) {
  const [input, setInput] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    onSendMessage(input.trim());
    setInput('');
  };

  return (
    <div style={styles.wrapper}>
      <style>{`
        @keyframes rc-fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .rc-msg { animation: rc-fadeIn 0.2s ease-out; }
        .rc-emoji-btn { transition: transform 0.1s; }
        .rc-emoji-btn:hover { transform: scale(1.25); }
        .rc-input:focus { border-color: #D4537E !important; outline: none; }
        .rc-send-btn:hover { background: #c0456f !important; }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.headerLabel}>Live Chat</span>
          <span style={styles.headerDot} />
          <span style={styles.headerCount}>{members.length} in room</span>
        </div>
        {/* Member avatars */}
        <div style={styles.avatarRow}>
          {members.slice(0, 5).map(m => (
            <div
              key={m.username}
              title={m.username}
              style={{
                ...styles.avatar,
                background: `hsl(${m.username.charCodeAt(0) * 15}, 55%, 32%)`,
              }}
            >
              {m.username[0].toUpperCase()}
            </div>
          ))}
          {members.length > 5 && (
            <span style={styles.avatarMore}>+{members.length - 5}</span>
          )}
        </div>
      </div>

      {/* Reactions bar */}
      <div style={styles.reactionsBar}>
        {EMOJIS.map(emoji => (
          <button
            key={emoji}
            className="rc-emoji-btn"
            onClick={() => onSendReaction(emoji)}
            style={styles.emojiBtn}
          >{emoji}</button>
        ))}
      </div>

      {/* Messages */}
      <div style={styles.messages}>
        {chat.length === 0 ? (
          <div style={styles.emptyChat}>
            <p style={styles.emptyChatText}>No messages yet</p>
            <p style={styles.emptyChatSub}>Say hi! 👋</p>
          </div>
        ) : (
          chat.map((msg, i) => (
            <div key={i} className="rc-msg" style={styles.msgRow}>
              <div
                style={{
                  ...styles.msgAvatar,
                  background: `hsl(${msg.username.charCodeAt(0) * 15}, 55%, 32%)`,
                }}
              >
                {msg.username[0].toUpperCase()}
              </div>
              <div style={styles.msgBody}>
                <div style={styles.msgMeta}>
                  <span style={styles.msgUsername}>@{msg.username}</span>
                  {msg.username === host && (
                    <span style={styles.hostTag}>HOST</span>
                  )}
                  {msg.username === username && (
                    <span style={styles.youTag}>you</span>
                  )}
                </div>
                <div style={styles.msgText}>{msg.message}</div>
              </div>
            </div>
          ))
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} style={styles.inputRow}>
        <input
          className="rc-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Say something..."
          maxLength={200}
          style={styles.input}
        />
        <button
          type="submit"
          className="rc-send-btn"
          disabled={!input.trim()}
          style={{ ...styles.sendBtn, opacity: input.trim() ? 1 : 0.45 }}
        >↑</button>
      </form>
    </div>
  );
}

const styles = {
  wrapper: {
    display: 'flex', flexDirection: 'column',
    height: '100%',
    background: 'rgba(255,255,255,0.015)',
    border: '0.5px solid rgba(212,83,126,0.12)',
    borderRadius: 16, overflow: 'hidden',
  },

  // Header
  header: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px 10px',
    borderBottom: '0.5px solid rgba(212,83,126,0.1)',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex', alignItems: 'center', gap: 6,
  },
  headerLabel: {
    fontSize: 10, letterSpacing: '0.18em',
    textTransform: 'uppercase', color: 'rgba(212,83,126,0.7)',
    fontWeight: 600,
  },
  headerDot: {
    width: 3, height: 3, borderRadius: '50%',
    background: 'rgba(212,83,126,0.4)',
  },
  headerCount: {
    fontSize: 10, color: 'rgba(245,230,237,0.3)',
    letterSpacing: '0.06em',
  },
  avatarRow: {
    display: 'flex', alignItems: 'center', gap: 3,
  },
  avatar: {
    width: 22, height: 22, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 9, fontWeight: 700, color: '#fff',
    border: '1.5px solid #080608', flexShrink: 0,
  },
  avatarMore: {
    fontSize: 9, color: 'rgba(245,230,237,0.3)',
    marginLeft: 2,
  },

  // Reactions
  reactionsBar: {
    display: 'flex', justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: '0.5px solid rgba(212,83,126,0.08)',
    flexShrink: 0,
  },
  emojiBtn: {
    fontSize: 17, background: 'none', border: 'none',
    cursor: 'pointer', padding: '2px 3px', lineHeight: 1,
    borderRadius: 6,
  },

  // Messages
  messages: {
    flex: 1, overflowY: 'auto',
    padding: '10px 12px',
    display: 'flex', flexDirection: 'column', gap: 10,
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(212,83,126,0.2) transparent',
  },
  emptyChat: {
    flex: 1, display: 'flex',
    flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '30px 0',
  },
  emptyChatText: {
    fontSize: 12, color: 'rgba(245,230,237,0.2)',
    marginBottom: 4,
  },
  emptyChatSub: {
    fontSize: 18,
  },
  msgRow: {
    display: 'flex', gap: 8, alignItems: 'flex-start',
  },
  msgAvatar: {
    width: 22, height: 22, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 9, fontWeight: 700, color: '#fff',
    flexShrink: 0, marginTop: 1,
  },
  msgBody: {
    flex: 1, minWidth: 0,
  },
  msgMeta: {
    display: 'flex', alignItems: 'center',
    gap: 5, marginBottom: 2,
  },
  msgUsername: {
    fontSize: 11, color: '#D4537E', fontWeight: 600,
  },
  hostTag: {
    fontSize: 9, color: 'rgba(212,83,126,0.55)',
    letterSpacing: '0.1em', textTransform: 'uppercase',
    background: 'rgba(212,83,126,0.08)',
    borderRadius: 3, padding: '1px 4px',
  },
  youTag: {
    fontSize: 9, color: 'rgba(245,230,237,0.25)',
    letterSpacing: '0.08em',
  },
  msgText: {
    fontSize: 13, color: 'rgba(245,230,237,0.72)',
    lineHeight: 1.45, wordBreak: 'break-word',
  },

  // Input
  inputRow: {
    display: 'flex', gap: 8,
    padding: '10px 12px',
    borderTop: '0.5px solid rgba(212,83,126,0.1)',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    background: 'rgba(255,255,255,0.04)',
    border: '0.5px solid rgba(212,83,126,0.25)',
    borderRadius: 10, padding: '9px 12px',
    fontSize: 13, color: '#f5e6ed',
    transition: 'border-color 0.2s',
  },
  sendBtn: {
    background: '#D4537E', color: '#fff',
    border: 'none', borderRadius: 10,
    padding: '9px 14px', cursor: 'pointer',
    fontSize: 14, transition: 'background 0.15s, opacity 0.15s',
    flexShrink: 0,
  },
};
